import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { callApiSecure, httpMethods } from '@caplend/cognito-auth';
import Layout from '../../components/Layout';
import ErrorToast from '../../components/ErrorToast';
import { QuoteBy, PaymentPurpose } from '../../enums';
import Cognito from '../../auth/cognito';
import * as balancesActions from '../../store/actions/balances';
import * as recipientsActions from '../../store/actions/recipients';
import * as crossBorderActions from '../../store/actions/crossBorder';
import * as transactionsActions from '../../store/actions/transactions';
import * as userActions from '../../store/actions/user';
import { CloudUploadIcon, CheckCircleIcon } from '../../assets/icons';
import SendMoneyAmountStep from './components/SendMoneyAmountStep';
import SendMoneyEntryStep from './components/SendMoneyEntryStep';
import SendMoneyReviewStep from './components/SendMoneyReviewStep';
import {
  matchRecipientFromExtractedData,
  canRequestQuoteFromParsedFields,
  buildInvoiceFormState,
  getCrossBorderQuoteAmounts,
  getInvoiceRecipientMatchError,
  getParsedInvoiceCurrency,
  applyInvoiceExtractedDataToForm,
  normalizePaymentPurpose,
  parseFormattedAmount as parseInvoiceAmount,
} from '../../utils/sendMoneyInvoiceHelpers';
import { parseTransactionDocument } from '../../store/sagas/api';
import FlagIcon from '../../components/FlagIcon';
import { formatAccountDisplay, getCountryInfo } from '../../utils/recipientHelpers';
import { getDocumentParseErrorMessage, getDocumentUploadValidationError } from '../../utils/documentUploadHelpers';
import {
  buildCrossBorderExecutePayload,
  getCrossBorderExecuteErrorMessage,
  getCrossBorderQuoteId,
  isComplianceException,
  isCrossBorderQuoteExpired,
  isDuplicateExecuteError,
} from '../../utils/crossBorderHelpers';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import SendMoneyStepper from './components/SendMoneyStepper';
import RecipientSelectStep from './components/RecipientSelectStep';
import SendMoneyTransferProgressStep from './components/SendMoneyTransferProgressStep';

const PAYMENT_REFERENCE_PATTERN = /^[a-zA-Z0-9 \-:]+$/;
const isPaymentReferenceValid = (value) => !value || !value.trim() || PAYMENT_REFERENCE_PATTERN.test(value.trim());

function parseCalculatorPrefillFromSearchParams(searchParams) {
  const currency = searchParams.get('currency');
  const sendAmountParam = searchParams.get('sendAmount');
  const receiveAmountParam = searchParams.get('receiveAmount');
  const quoteByParam = searchParams.get('quoteBy');
  if (!currency && !sendAmountParam && !receiveAmountParam) return null;

  return {
    currency: currency ? currency.toUpperCase().trim() : null,
    sendAmount: sendAmountParam || '',
    receiveAmount: receiveAmountParam || '',
    quoteBy: quoteByParam === 'receive' ? 'receive' : 'send',
  };
}

function SendMoney() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recipientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCalculatorPrefill = parseCalculatorPrefillFromSearchParams(searchParams);
  const {
    preferences,
    formatNumber,
    formatCurrency: formatCurrencyAmount,
    formatAmountInput,
    parseAmount,
  } = useNumberFormat();

  const { recipients: rawRecipients, gettingRecipients } = useSelector((state) => state.recipients);
  const { transactions } = useSelector((state) => state.transactions);
  const { currencies: configCurrencies } = useSelector((state) => state.config);
  const { balances } = useSelector((state) => state.balances);
  const { spotRate, gettingSpotRate, crossBorderQuote, gettingCrossBorderQuote, getCrossBorderQuoteError } = useSelector(
    (state) => state.crossBorder,
  );

  const [currentStep, setCurrentStep] = useState(() => {
    if (recipientId) return 2;
    if (initialCalculatorPrefill) return 1;
    return 0;
  });
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [receiveAmount, setReceiveAmount] = useState('');
  const [sendCurrency, setSendCurrency] = useState('EUR');
  const [receiveCurrency, setReceiveCurrency] = useState('EUR');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentPurpose, setPaymentPurpose] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [quoteRequested, setQuoteRequested] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [executingTransfer, setExecutingTransfer] = useState(false);
  const [sendError, setSendError] = useState('');
  const [complianceBlockedQuoteId, setComplianceBlockedQuoteId] = useState(null);
  const [transferRequestId, setTransferRequestId] = useState(null);
  const [transferSteps, setTransferSteps] = useState([]);
  const [transactionDocumentType, setTransactionDocumentType] = useState('');
  const [transactionDocumentId, setTransactionDocumentId] = useState('');
  const [transactionDocumentFile, setTransactionDocumentFile] = useState(null);
  const [transactionDocumentFileName, setTransactionDocumentFileName] = useState('');
  const [parsingDocument, setParsingDocument] = useState(false);
  const [parsingError, setParsingError] = useState('');
  const [parseSuccessInfo, setParseSuccessInfo] = useState(null);
  // Locked pricing method for this screen: maps to QuoteBy.SendAmount | QuoteBy.ReceiveAmount.
  const [quotePricingField, setQuotePricingField] = useState(null); // null | 'send' | 'receive'
  const isUpdatingFromUrlRef = useRef(false);
  const preserveFormOnRecipientChangeRef = useRef(false);
  const [quoteInputsSnapshot, setQuoteInputsSnapshot] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [arrowAnimated, setArrowAnimated] = useState(false);
  const [calculatorPrefill, setCalculatorPrefill] = useState(initialCalculatorPrefill);
  const [fromDashboardCalculator] = useState(Boolean(initialCalculatorPrefill));
  const [calculatorDestinationCurrency] = useState(() => initialCalculatorPrefill?.currency || null);
  const calculatorPrefillAppliedRef = useRef(false);
  const calculatorPrefillParsedRef = useRef(Boolean(initialCalculatorPrefill));
  const executeInFlightRef = useRef(false);
  const activeExecuteRequestIdRef = useRef(null);
  const [amountStartInEditMode, setAmountStartInEditMode] = useState(false);
  const [pendingInvoiceExtractedData, setPendingInvoiceExtractedData] = useState(null);

  const recipients = Array.isArray(rawRecipients) ? rawRecipients : [];
  const selectedRecipientData = recipients.find((r) => r.id === selectedRecipient);
  const isSwiftTransfer = selectedRecipientData?.type === 'international_swift';

  const getReceiveCurrenciesForRecipient = (recipient) => {
    if (!recipient?.currency) return [];
    return [{ code: recipient.currency }];
  };

  const getSendCurrenciesForRecipient = (recipient) => {
    const currencies = [{ code: 'EUR' }, { code: 'MXN' }];
    if (!recipient?.currency) return currencies;
    return currencies.filter((c) => c.code === 'EUR' || c.code === recipient.currency);
  };

  const sendCurrencies = getSendCurrenciesForRecipient(selectedRecipientData);
  const receiveCurrencies = getReceiveCurrenciesForRecipient(selectedRecipientData);

  const selectedCountryInfo = useMemo(
    () => (selectedRecipientData ? getCountryInfo(selectedRecipientData, configCurrencies) : null),
    [selectedRecipientData, configCurrencies],
  );

  const destinationCountryForGlobe = useMemo(() => {
    const code =
      selectedRecipientData?.country?.code ||
      selectedRecipientData?.countryCode ||
      selectedRecipientData?.bankCountryCode ||
      selectedCountryInfo?.code ||
      null;
    if (!code || !Array.isArray(configCurrencies)) {
      return selectedRecipientData?.country || (selectedCountryInfo ? { ...selectedCountryInfo } : null);
    }
    const normalized = String(code).toUpperCase();
    let match = null;
    configCurrencies.some((currency) => {
      if (currency?.country?.code && String(currency.country.code).toUpperCase() === normalized) {
        match = currency.country;
        return true;
      }
      if (Array.isArray(currency?.countries)) {
        const nested = currency.countries.find((c) => c?.code && String(c.code).toUpperCase() === normalized);
        if (nested) {
          match = nested;
          return true;
        }
      }
      return false;
    });
    if (match) return match;
    return selectedRecipientData?.country || (selectedCountryInfo ? { name: selectedCountryInfo.name, code: selectedCountryInfo.code } : null);
  }, [selectedRecipientData, selectedCountryInfo, configCurrencies]);

  const formatPaymentPurposeLabel = (key) =>
    key
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());

  const paymentPurposeOptions = Object.keys(PaymentPurpose).map((key) => ({
    label: formatPaymentPurposeLabel(key),
    value: PaymentPurpose[key],
  }));

  useEffect(() => {
    if (currentStep !== 2) {
      setAmountStartInEditMode(false);
    }
  }, [currentStep]);

  useEffect(() => {
    dispatch(balancesActions.getBalances());
    dispatch(recipientsActions.getRecipients());
    dispatch(transactionsActions.getTransactions());
    dispatch(userActions.getBankAccount());
    dispatch(crossBorderActions.clearCrossBorderQuote());

    return () => {
      dispatch(crossBorderActions.clearCrossBorderQuote());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!recipients.length || !recipientId) return;

    const recipientExists = recipients.find((r) => r.id === recipientId);
    if (!recipientExists) {
      navigate('/send-money', { replace: true });
      return;
    }

    setCurrentStep((step) => {
      // Step 1 = recipient picker; do not push back to amount when user clicked Back
      if (step === 1) return step;
      if (step >= 2) return step;
      return 2;
    });

    if (currentStep !== 1 && selectedRecipient !== recipientId) {
      isUpdatingFromUrlRef.current = true;
      setSelectedRecipient(recipientId);
      isUpdatingFromUrlRef.current = false;
    }
  }, [recipientId, recipients, selectedRecipient, navigate, currentStep]);

  useEffect(() => {
    if (calculatorPrefillParsedRef.current) return;
    const urlPrefill = parseCalculatorPrefillFromSearchParams(searchParams);
    if (!urlPrefill && !calculatorPrefill) return;

    calculatorPrefillParsedRef.current = true;
    if (urlPrefill && !calculatorPrefill) {
      setCalculatorPrefill(urlPrefill);
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!next.get('currency') && !next.get('sendAmount') && !next.get('receiveAmount') && !next.get('quoteBy')) {
        return prev;
      }
      next.delete('currency');
      next.delete('sendAmount');
      next.delete('receiveAmount');
      next.delete('quoteBy');
      return next;
    });
  }, [searchParams, setSearchParams, calculatorPrefill]);

  const recipientCurrencyFilter = calculatorDestinationCurrency || calculatorPrefill?.currency || null;

  useEffect(() => {
    if (selectedRecipientData && selectedRecipientData.currency) {
      setReceiveCurrency(selectedRecipientData.currency);
    }
  }, [selectedRecipientData]);

  useEffect(() => {
    if (preserveFormOnRecipientChangeRef.current) {
      preserveFormOnRecipientChangeRef.current = false;
      if (selectedRecipientData?.currency) {
        setReceiveCurrency(selectedRecipientData.currency);
      }
      return;
    }

    setSendCurrency('EUR');
    if (!calculatorPrefillAppliedRef.current) {
      setSendAmount('');
      setReceiveAmount('');
      setQuotePricingField(null);
    }
    setParseSuccessInfo(null);
    setTransactionDocumentType('');
    setTransactionDocumentId('');
    setTransactionDocumentFile(null);
    setTransactionDocumentFileName('');
    setParsingError('');
    setPaymentReference('');
    setPaymentPurpose('');
    setQuoteInputsSnapshot(null);
    setQuoteRequested(false);
    setValidationError(null);
    setErrorMessage('');
    setArrowAnimated(false);
    setCurrentStep((step) => {
      if (step === 3) return 2;
      if (step === 0) return 0;
      return step;
    });
  }, [selectedRecipientData]);

  useEffect(() => {
    if (currentStep !== 2 || !calculatorPrefill || calculatorPrefillAppliedRef.current || !selectedRecipientData) {
      return;
    }

    calculatorPrefillAppliedRef.current = true;
    if (calculatorPrefill.quoteBy === 'receive' && calculatorPrefill.receiveAmount) {
      setReceiveAmount(calculatorPrefill.receiveAmount);
      setQuotePricingField('receive');
    } else if (calculatorPrefill.sendAmount) {
      setSendAmount(calculatorPrefill.sendAmount);
      setQuotePricingField('send');
    }
    setCalculatorPrefill(null);
  }, [currentStep, calculatorPrefill, selectedRecipientData]);

  useEffect(() => {
    if (
      currentStep === 2 &&
      quoteRequested &&
      crossBorderQuote &&
      !gettingCrossBorderQuote &&
      sendCurrency !== receiveCurrency
    ) {
      setCurrentStep(3);
    }
  }, [currentStep, quoteRequested, crossBorderQuote, gettingCrossBorderQuote, sendCurrency, receiveCurrency]);

  const requestCrossBorderQuote = ({ recipientId, recipientData, formState, file, fileName }) => {
    const pricingField =
      formState.quotePricingField === 'receive' || formState.lastEditedField === 'receive' ? 'receive' : 'send';
    const quoteBy = pricingField === 'send' ? QuoteBy.SendAmount : QuoteBy.ReceiveAmount;
    const amount =
      pricingField === 'send'
        ? parseInvoiceAmount(formState.sendAmount, preferences)
        : parseInvoiceAmount(formState.receiveAmount, preferences);

    dispatch(crossBorderActions.clearCrossBorderQuote());
    activeExecuteRequestIdRef.current = null;
    setQuoteRequested(true);
    setCountdown(null);
    setErrorMessage('');
    setValidationError(null);

    const quotePayload = {
      offRampProvider: recipientData?.offRampProvider,
      recipientId,
      amount,
      quoteBy,
      paymentReference: formState.paymentReference,
      paymentPurpose: formState.paymentPurpose,
    };

    if (file) {
      quotePayload.transactionDocument = {
        name: fileName,
        type: formState.transactionDocumentType,
        id: formState.transactionDocumentId,
        file,
      };
    }

    setQuoteInputsSnapshot({
      selectedRecipient: recipientId,
      sendAmount: formState.sendAmount,
      receiveAmount: formState.receiveAmount,
      sendCurrency: formState.sendCurrency,
      receiveCurrency: formState.receiveCurrency,
      paymentReference: formState.paymentReference.trim(),
      paymentPurpose: formState.paymentPurpose,
      paymentDocument: fileName,
      quotePricingField: pricingField,
      transactionDocumentFileName: fileName,
    });

    dispatch(crossBorderActions.getCrossBorderQuote(quotePayload));
  };

  const applyParsedDocumentData = (data, recipientForCurrencies) => {
    const sendCodes = getSendCurrenciesForRecipient(recipientForCurrencies).map((c) => c.code);
    const receiveCodes = getReceiveCurrenciesForRecipient(recipientForCurrencies).map((c) => c.code);
    const effectiveReceiveCurrency = recipientForCurrencies?.currency || receiveCurrency;
    const docType = data.documentType ?? data.swift_document_type;
    const docId = data.documentId ?? data.swift_invoice_id ?? data.paymentReference;
    let fieldsCount = 0;

    const parsedCurrency = data.currency ? String(data.currency).trim().toUpperCase() : null;
    const isSendCurrency = parsedCurrency && sendCodes.includes(parsedCurrency);
    const isReceiveCurrency = parsedCurrency && receiveCodes.includes(parsedCurrency);
    const currencyMatches = isSendCurrency || isReceiveCurrency;

    if (docType) {
      setTransactionDocumentType(docType);
      fieldsCount += 1;
    }

    if (docId) {
      setTransactionDocumentId(docId);
      fieldsCount += 1;
    }

    if (data.amount != null && !Number.isNaN(Number(data.amount)) && currencyMatches) {
      const amountNum = Number(data.amount);
      if (isSendCurrency) {
        setSendAmount(formatNumber(amountNum));
        setQuotePricingField((current) => current || 'send');
        if (parsedCurrency && sendCurrency !== parsedCurrency) {
          setSendCurrency(parsedCurrency);
        }
        if (parsedCurrency === effectiveReceiveCurrency) {
          setReceiveAmount(formatNumber(amountNum));
        } else if (spotRate) {
          setReceiveAmount(formatNumber(amountNum * spotRate));
        }
        fieldsCount += 1;
      } else {
        setReceiveAmount(formatNumber(amountNum));
        setQuotePricingField((current) => current || 'receive');
        if (sendCurrency !== effectiveReceiveCurrency && spotRate) {
          setSendAmount(formatNumber(amountNum / spotRate));
        } else if (sendCurrency === effectiveReceiveCurrency) {
          setSendAmount(formatNumber(amountNum));
        }
        fieldsCount += 1;
      }
    } else if (parsedCurrency && !currencyMatches) {
      setParsingError('Currencies did not match.');
    }

    if (data.paymentReference && typeof data.paymentReference === 'string') {
      setPaymentReference(data.paymentReference);
      fieldsCount += 1;
    }

    if (data.paymentPurpose) {
      const normalized = normalizePaymentPurpose(data.paymentPurpose);
      if (normalized) {
        setPaymentPurpose(normalized);
        fieldsCount += 1;
      }
    }

    return { fieldsCount, parsedCurrency, currencyMatches };
  };

  const handleDocumentUpload = async (file, { fromEntry = false } = {}) => {
    if (!file) return;

    const uploadValidationError = getDocumentUploadValidationError(file);
    if (uploadValidationError) {
      setParseSuccessInfo(null);
      setParsingError(uploadValidationError);
      setParsingDocument(false);
      return;
    }

    setParseSuccessInfo(null);
    setPendingInvoiceExtractedData(null);
    setTransactionDocumentType('');
    setTransactionDocumentId('');
    setTransactionDocumentFileName(file.name);
    setTransactionDocumentFile(file);
    setParsingError('');
    setParsingDocument(true);

    try {
      const result = await parseTransactionDocument(file);

      if (result.status === 'success' && result.extracted_data) {
        const data = result.extracted_data;
        let matchedRecipient = selectedRecipientData;

        if (fromEntry) {
          const parsedCurrency = getParsedInvoiceCurrency(data);
          const matchedId = matchRecipientFromExtractedData(data, recipients);
          if (!matchedId) {
            setPendingInvoiceExtractedData(data);
            setParsingError(getInvoiceRecipientMatchError(recipients, { parsedCurrency }));
            setSelectedRecipient('');
            setCurrentStep(1);
            navigate('/send-money', { replace: true });
            return;
          }

          matchedRecipient = recipients.find((r) => r.id === matchedId) || null;
          const formState = buildInvoiceFormState(data, matchedRecipient, { formatNumber, spotRate });

          if (formState.currencyMismatch) {
            setPendingInvoiceExtractedData(data);
            setParsingError(
              `Invoice currency (${formState.parsedCurrency}) does not match the selected recipient currency (${matchedRecipient?.currency}). Select a ${formState.parsedCurrency} recipient or add a new one.`,
            );
            setSelectedRecipient('');
            setCurrentStep(1);
            navigate('/send-money', { replace: true });
            return;
          }

          preserveFormOnRecipientChangeRef.current = true;
          isUpdatingFromUrlRef.current = true;
          setSelectedRecipient(matchedId);
          setReceiveCurrency(formState.receiveCurrency);
          setSendCurrency(formState.sendCurrency);
          setSendAmount(formState.sendAmount);
          setReceiveAmount(formState.receiveAmount);
          setQuotePricingField(formState.lastEditedField === 'receive' ? 'receive' : 'send');
          setPaymentReference(formState.paymentReference);
          setPaymentPurpose(formState.paymentPurpose);
          setTransactionDocumentType(formState.transactionDocumentType);
          setTransactionDocumentId(formState.transactionDocumentId);
          navigate(`/send-money/${matchedId}`, { replace: true });
          isUpdatingFromUrlRef.current = false;

          const isSwift = matchedRecipient?.type === 'international_swift';
          const canQuote = canRequestQuoteFromParsedFields({
            selectedRecipient: matchedId,
            sendAmount: formState.sendAmount,
            receiveAmount: formState.receiveAmount,
            paymentReference: formState.paymentReference,
            paymentPurpose: formState.paymentPurpose,
            sendCurrency: formState.sendCurrency,
            receiveCurrency: formState.receiveCurrency,
            isSwiftTransfer: isSwift,
            transactionDocumentType: formState.transactionDocumentType,
            transactionDocumentId: formState.transactionDocumentId,
            transactionDocumentFile: file,
          });

          if (canQuote) {
            setCurrentStep(3);
            setParsingError('');
            requestCrossBorderQuote({
              recipientId: matchedId,
              recipientData: matchedRecipient,
              formState,
              file,
              fileName: file.name,
            });
            setParseSuccessInfo({
              fileName: file.name || 'Document',
              fieldsCount: 1,
            });
          } else if (!formState.sendAmount && !formState.receiveAmount) {
            setParsingError('We could not read the amount from this document. Please enter the amount manually.');
            setCurrentStep(2);
          } else {
            setParsingError('Some details could not be read from the document. Please check the amount step.');
            setCurrentStep(2);
          }
          return;
        }

        const { fieldsCount, parsedCurrency, currencyMatches } = applyParsedDocumentData(data, matchedRecipient);

        setParseSuccessInfo({
          fileName: file.name || 'Document',
          fieldsCount,
        });
        if (fieldsCount === 0 && !parsedCurrency) {
          setParsingError('Document parsing completed but no structured data was extracted.');
        } else if (fieldsCount === 0 && parsedCurrency && !currencyMatches) {
          setParsingError('Currencies did not match.');
        }
      } else {
        setParsingError('Document parsing completed but no structured data was extracted.');
      }
    } catch (error) {
      setParsingError(getDocumentParseErrorMessage(error));
    } finally {
      setParsingDocument(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleDocumentUpload(file);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) handleDocumentUpload(file);
  };

  const getParseSuccessMessage = (info) => {
    if (info.fieldsCount > 0) {
      const fieldWord = info.fieldsCount === 1 ? 'field' : 'fields';
      return `Document parsed successfully. ${info.fieldsCount} ${fieldWord} populated.`;
    }
    return 'Document parsed successfully but no details could be extracted.';
  };

  const renderUploadContent = () => {
    if (parsingDocument) {
      return (
        <>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2' />
          <p className='text-sm text-primary font-medium'>Analysing with AI</p>
        </>
      );
    }
    if (parseSuccessInfo) {
      return (
        <>
          <CheckCircleIcon className='h-8 w-8 text-green-600 mb-2' />
          <p className='text-sm text-foreground font-medium'>{parseSuccessInfo.fileName}</p>
          <p className='text-xs text-muted-foreground mt-1'>{getParseSuccessMessage(parseSuccessInfo)}</p>
        </>
      );
    }
    return (
      <>
        <CloudUploadIcon className='h-8 w-8 text-muted-foreground mb-2' />
        {transactionDocumentFileName ? (
          <p className='text-sm text-foreground font-medium'>{transactionDocumentFileName}</p>
        ) : (
          <p className='text-sm text-muted-foreground'>Click here or drag file to upload</p>
        )}
      </>
    );
  };

  useEffect(() => {
    if (sendCurrency && receiveCurrency && sendCurrency !== receiveCurrency) {
      dispatch(crossBorderActions.getSpotRate({
        currencyPair: `${sendCurrency}${receiveCurrency}`,
        recipientId: selectedRecipient,
      }));
    }
  }, [sendCurrency, receiveCurrency, selectedRecipient, dispatch]);

  const calculateCountdown = (expiryTime) => {
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diffMs = expiry - now;

    if (diffMs <= 0) {
      return null;
    }

    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return { minutes, seconds };
  };

  const formatNumberInput = (value) => formatAmountInput(value);

  const parseFormattedNumber = (value) => parseAmount(value);

  useEffect(() => {
    if (!crossBorderQuote?.expiry) {
      setCountdown(null);
      return undefined;
    }

    const tickCountdown = () => {
      const newCountdown = calculateCountdown(crossBorderQuote.expiry);
      setCountdown(newCountdown);
      return newCountdown;
    };

    tickCountdown();

    const intervalId = setInterval(() => {
      if (!tickCountdown()) clearInterval(intervalId);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [crossBorderQuote?.expiry]);

  const deriveReceiveFromSend = (sendValue) => {
    const sendAmountNum = parseFormattedNumber(sendValue);
    if (!sendAmountNum) return '';
    if (sendCurrency === receiveCurrency) return formatNumber(sendAmountNum);
    if (!spotRate) return '';
    return formatNumber(sendAmountNum * spotRate);
  };

  const deriveSendFromReceive = (receiveValue) => {
    const receiveAmountNum = parseFormattedNumber(receiveValue);
    if (!receiveAmountNum) return '';
    if (sendCurrency === receiveCurrency) return formatNumber(receiveAmountNum);
    if (!spotRate) return '';
    return formatNumber(receiveAmountNum / spotRate);
  };

  let authoritativeAmount = null;
  if (quotePricingField === 'receive') {
    authoritativeAmount = receiveAmount;
  } else if (quotePricingField === 'send') {
    authoritativeAmount = sendAmount;
  }

  useEffect(() => {
    if (!quotePricingField || !spotRate) return;
    if (quoteRequested && crossBorderQuote) return;

    if (quotePricingField === 'send' && sendAmount) {
      const derivedReceive = deriveReceiveFromSend(sendAmount);
      if (derivedReceive) {
        setReceiveAmount(derivedReceive);
      } else if (sendCurrency !== receiveCurrency) {
        setReceiveAmount('');
      }
      return;
    }

    if (quotePricingField === 'receive' && receiveAmount) {
      const derivedSend = deriveSendFromReceive(receiveAmount);
      if (derivedSend) {
        setSendAmount(derivedSend);
      } else if (sendCurrency !== receiveCurrency) {
        setSendAmount('');
      }
    }
  }, [sendCurrency, receiveCurrency, spotRate, quotePricingField, authoritativeAmount, quoteRequested, crossBorderQuote]);
  useEffect(() => {
    if (sendCurrency !== receiveCurrency && (sendAmount || receiveAmount) && !arrowAnimated) {
      setArrowAnimated(true);
    }
  }, [sendCurrency, receiveCurrency, sendAmount, receiveAmount, arrowAnimated]);

  const sendAmountNum = parseFormattedNumber(sendAmount);
  const receiveAmountNum = parseFormattedNumber(receiveAmount);

  const handleSendAmountChange = (value) => {
    const formatted = formatNumberInput(value);
    setQuotePricingField('send');
    setSendAmount(formatted);

    if (!value || parseFormattedNumber(formatted) === 0) {
      setReceiveAmount('');
      return;
    }

    setReceiveAmount(deriveReceiveFromSend(formatted));
  };

  const handleReceiveAmountChange = (value) => {
    const formatted = formatNumberInput(value);
    setQuotePricingField('receive');
    setReceiveAmount(formatted);

    if (!value || parseFormattedNumber(formatted) === 0) {
      setSendAmount('');
      return;
    }

    setSendAmount(deriveSendFromReceive(formatted));
  };

  const quoteAmounts = getCrossBorderQuoteAmounts(crossBorderQuote);
  const {
    totalFee: transferFee,
    amountSend: totalCost,
    amountReceive,
  } = quoteAmounts;

  useEffect(() => {
    if (!crossBorderQuote || !quoteRequested) return;

    const { amountSend, amountReceive: quotedReceive } = getCrossBorderQuoteAmounts(crossBorderQuote);
    const nextSend = amountSend != null && amountSend > 0 ? formatNumber(amountSend) : null;
    const nextReceive = quotedReceive != null && quotedReceive > 0 ? formatNumber(quotedReceive) : null;

    if (nextSend) {
      setSendAmount(nextSend);
    }
    if (nextReceive) {
      setReceiveAmount(nextReceive);
    }

    if (nextSend || nextReceive) {
      setQuoteInputsSnapshot((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(nextSend ? { sendAmount: nextSend } : {}),
          ...(nextReceive ? { receiveAmount: nextReceive } : {}),
          quotePricingField: quotePricingField || prev.quotePricingField || 'send',
        };
      });
    }
  }, [crossBorderQuote, quoteRequested, quotePricingField]);

  const quoteInputsChanged =
    quoteInputsSnapshot &&
    quoteRequested &&
    crossBorderQuote &&
    (quoteInputsSnapshot.selectedRecipient !== selectedRecipient ||
      quoteInputsSnapshot.sendAmount !== sendAmount ||
      quoteInputsSnapshot.receiveAmount !== receiveAmount ||
      quoteInputsSnapshot.sendCurrency !== sendCurrency ||
      quoteInputsSnapshot.receiveCurrency !== receiveCurrency ||
      quoteInputsSnapshot.paymentReference !== paymentReference.trim() ||
      quoteInputsSnapshot.paymentPurpose !== paymentPurpose ||
      quoteInputsSnapshot.quotePricingField !== (quotePricingField || 'send') ||
      quoteInputsSnapshot.transactionDocumentFileName !== transactionDocumentFileName);

  const applyPendingInvoiceToRecipient = (recipient) => {
    if (!pendingInvoiceExtractedData || !recipient) return;

    const applied = applyInvoiceExtractedDataToForm(pendingInvoiceExtractedData, recipient, {
      formatNumber,
      spotRate,
    });
    if (!applied) return;

    preserveFormOnRecipientChangeRef.current = true;
    setReceiveCurrency(applied.receiveCurrency);
    setSendCurrency(applied.sendCurrency);
    setSendAmount(applied.sendAmount);
    setReceiveAmount(applied.receiveAmount);
    setQuotePricingField(applied.lastEditedField === 'receive' ? 'receive' : 'send');
    setPaymentReference(applied.paymentReference);
    setPaymentPurpose(applied.paymentPurpose);
    setTransactionDocumentType(applied.transactionDocumentType);
    setTransactionDocumentId(applied.transactionDocumentId);
    setPendingInvoiceExtractedData(null);

    let invoiceError = '';
    if (applied.currencyMismatch) {
      invoiceError = `Invoice currency (${applied.parsedCurrency}) does not match this recipient's currency (${recipient.currency}). Choose a matching recipient.`;
    } else if (!applied.hasAmount) {
      invoiceError = 'We could not read the amount from your invoice. Please enter the amount manually.';
    }
    setParsingError(invoiceError);
    if (applied.hasAmount) {
      setParseSuccessInfo({
        fileName: transactionDocumentFileName || 'Document',
        fieldsCount: 1,
      });
    }
  };

  const handleSelectRecipient = (id) => {
    const recipient = recipients.find((r) => r.id === id);
    if (recipientCurrencyFilter && recipient?.currency && recipient.currency.toUpperCase() !== recipientCurrencyFilter) {
      setErrorMessage(`This recipient cannot receive ${recipientCurrencyFilter}. Choose another recipient or add a new one.`);
      return;
    }

    isUpdatingFromUrlRef.current = true;
    setSelectedRecipient(id);
    applyPendingInvoiceToRecipient(recipient);
    setCurrentStep(2);
    navigate(`/send-money/${id}`, { replace: true });
    isUpdatingFromUrlRef.current = false;
  };

  const handleEditAmountFromReview = () => {
    dispatch(crossBorderActions.clearCrossBorderQuote());
    setQuoteRequested(false);
    setCountdown(null);
    setAmountStartInEditMode(true);
    setCurrentStep(2);
  };

  const handleBack = () => {
    if (currentStep === 4) {
      return;
    }
    if (currentStep === 0) {
      navigate('/dashboard');
      return;
    }
    if (currentStep === 3) {
      dispatch(crossBorderActions.clearCrossBorderQuote());
      activeExecuteRequestIdRef.current = null;
      setQuoteRequested(false);
      setCountdown(null);
      setAmountStartInEditMode(false);
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      navigate('/send-money', { replace: true });
      setSelectedRecipient('');
      setCurrentStep(1);
      return;
    }
    if (currentStep === 1) {
      setSelectedRecipient('');
      if (fromDashboardCalculator) {
        navigate('/dashboard');
        return;
      }
      setCurrentStep(0);
      navigate('/send-money', { replace: true });
      return;
    }
    navigate('/dashboard');
  };

  const handleTransferBackHome = () => {
    dispatch(crossBorderActions.clearCrossBorderQuote());
    setTransferRequestId(null);
    setTransferSteps([]);
    setQuoteRequested(false);
    setCountdown(null);
    setSelectedRecipient('');
    if (fromDashboardCalculator) {
      navigate('/dashboard');
      return;
    }
    setCurrentStep(0);
    navigate('/send-money', { replace: true });
  };

  const handleStartFromScratch = () => {
    setPendingInvoiceExtractedData(null);
    setParsingError('');
    setCurrentStep(1);
  };

  const handleEntryFileSelect = (file) => {
    handleDocumentUpload(file, { fromEntry: true });
  };

  const stepMetaByStep = {
    1: {
      title: 'Who are you sending to?',
      subtitle: recipientCurrencyFilter
        ? `Showing recipients who can receive ${recipientCurrencyFilter}.`
        : null,
    },
    2: {
      title: 'How much do you want to send?',
      subtitle: null,
    },
    3: {
      title: 'Review & send',
      subtitle: 'Please confirm the details below.',
    },
  };
  const stepMeta = stepMetaByStep[currentStep] || stepMetaByStep[1];
  const pageTitle = currentStep === 0 ? 'How would you like to send?' : stepMeta.title;
  const backLabel = currentStep === 0 ? 'Back to home' : 'Back';
  const floatBackConfig =
    currentStep === 4
      ? { onClick: () => navigate('/dashboard'), label: 'Back to home', ariaLabel: 'Back to home' }
      : { onClick: handleBack, label: backLabel };

  const showFxBar = sendCurrency !== receiveCurrency;
  const canContinueToReview =
    Boolean(selectedRecipient) &&
    Boolean(sendAmount || receiveAmount) &&
    sendCurrency !== receiveCurrency &&
    Boolean(paymentReference?.trim()) &&
    isPaymentReferenceValid(paymentReference) &&
    Boolean(paymentPurpose?.trim()) &&
    (!isSwiftTransfer || Boolean(transactionDocumentType && transactionDocumentId && transactionDocumentFile));

  const paymentPurposeLabel = useMemo(() => {
    const match = paymentPurposeOptions.find((option) => option.value === paymentPurpose);
    return match?.label || paymentPurpose || '';
  }, [paymentPurpose, paymentPurposeOptions]);

  const countdownDisplay = countdown
    ? `${String(countdown.minutes).padStart(2, '0')}:${String(countdown.seconds).padStart(2, '0')}`
    : null;

  const exchangeRateValue = quoteAmounts.spotRate ?? spotRate ?? 1;
  const exchangeRateLabel = `1 ${sendCurrency} = ${formatNumber(Number(exchangeRateValue), {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })} ${receiveCurrency}`;

  const feePercent = useMemo(() => {
    const fee = transferFee ?? 0;
    const base = totalCost ?? sendAmountNum ?? 0;
    if (!base || !fee) return null;
    const pct = Math.round((fee / base) * 1000) / 10;
    return pct > 0 ? pct : null;
  }, [transferFee, totalCost, sendAmountNum]);

  const hasReviewQuote = Boolean(quoteRequested && crossBorderQuote);
  const exchangeRateSubLabel = hasReviewQuote ? 'Quoted rate (includes fees)' : null;
  const isLoadingReviewQuote = currentStep === 3 && quoteRequested && !hasReviewQuote;

  const crossBorderQuoteId = getCrossBorderQuoteId(crossBorderQuote);
  const isComplianceBlocked = Boolean(crossBorderQuoteId) && crossBorderQuoteId === complianceBlockedQuoteId;

  useEffect(() => {
    setSendError('');
  }, [crossBorderQuoteId]);

  const canSendTransfer =
    Boolean(selectedRecipient) &&
    Boolean(sendAmount || receiveAmount) &&
    !gettingCrossBorderQuote &&
    Boolean(countdown) &&
    !executingTransfer &&
    !quoteInputsChanged &&
    !isComplianceBlocked &&
    Boolean(crossBorderQuote);

  const isQuoteExpired = Boolean(
    crossBorderQuote?.expiry && new Date(crossBorderQuote.expiry).getTime() <= Date.now(),
  );

  const sendDisabledReason = (() => {
    if (canSendTransfer) return null;
    if (executingTransfer) return null;
    if (gettingCrossBorderQuote) return 'Updating your quote…';
    if (!crossBorderQuote) return null;
    if (isQuoteExpired) return 'This quote has expired. Get a new quote to continue.';
    if (quoteInputsChanged) return 'Transfer details have changed. Get a new quote to continue.';
    return null;
  })();

  const renderPageSubtitle = () => {
    if (currentStep === 2 && selectedCountryInfo) {
      return (
        <p className='m-0 mt-3 flex max-w-[560px] items-center gap-2 text-sm leading-[1.55] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
          <span>Sending to</span>
          {selectedRecipientData?.currency && (
            <span
              className='flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-[var(--color-border)] bg-[var(--off)]'
              aria-hidden='true'
            >
              <FlagIcon currencyCode={selectedRecipientData.currency} className='h-2.5 w-3.5 rounded-sm' />
            </span>
          )}
          <span>{selectedCountryInfo.name}</span>
        </p>
      );
    }
    if (stepMeta.subtitle) {
      return (
        <p className='m-0 mt-3 max-w-[560px] text-sm leading-[1.55] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
          {stepMeta.subtitle}
        </p>
      );
    }
    return null;
  };

  const handleGetQuote = () => {
    if (!selectedRecipient || (!sendAmount && !receiveAmount)) {
      setErrorMessage('Please select a recipient and enter an amount.');
      return;
    }

    if (!paymentReference || !paymentReference.trim()) {
      setErrorMessage('Payment description is required.');
      return;
    }

    if (!isPaymentReferenceValid(paymentReference)) {
      setErrorMessage('Payment reference can only contain letters, numbers, spaces, dashes, and colons.');
      return;
    }

    if (!paymentPurpose || !paymentPurpose.trim()) {
      setErrorMessage('Payment purpose is required.');
      return;
    }

    if (selectedRecipientData?.type === 'international_swift') {
      if (!transactionDocumentType || !transactionDocumentId || !transactionDocumentFile) {
        setErrorMessage('Please fill all transaction document fields for this transfer type.');
        return;
      }
    }
    requestCrossBorderQuote({
      recipientId: selectedRecipient,
      recipientData: selectedRecipientData,
      formState: {
        sendAmount,
        receiveAmount,
        sendCurrency,
        receiveCurrency,
        paymentReference,
        paymentPurpose,
        quotePricingField: quotePricingField || 'send',
        transactionDocumentType,
        transactionDocumentId,
      },
      file: transactionDocumentFile,
      fileName: transactionDocumentFileName,
    });
  };

  const handleSendMoney = async () => {
    if (executeInFlightRef.current || executingTransfer) {
      return;
    }

    if (!selectedRecipient || (!sendAmount && !receiveAmount)) {
      setErrorMessage('Please select a recipient and enter an amount.');
      return;
    }

    if (quoteInputsChanged) {
      setValidationError('Quote inputs have changed. Please get a new quote before sending money.');
      return;
    }

    if (!spotRate && sendCurrency !== receiveCurrency) {
      setErrorMessage('Please wait for the exchange rate to load.');
      return;
    }

    const totalAmount = totalCost;
    if (totalAmount > balances?.[sendCurrency]) {
      setErrorMessage(
        `Transfer amount (${formatCurrencyAmount(totalAmount, sendCurrency)}) exceeds your current balance (${formatCurrencyAmount(balances?.[sendCurrency], sendCurrency)}).`,
      );
      return;
    }

    if (isCrossBorderQuoteExpired(crossBorderQuote)) {
      setErrorMessage('This quote has expired. Please get a new quote before sending.');
      return;
    }

    const executePayload = buildCrossBorderExecutePayload(
      crossBorderQuote,
      activeExecuteRequestIdRef.current || uuidv4(),
    );
    if (!executePayload) {
      setErrorMessage('Quote is missing. Please go back and generate a new quote.');
      return;
    }

    const { requestId } = executePayload;
    activeExecuteRequestIdRef.current = requestId;
    executeInFlightRef.current = true;
    setExecutingTransfer(true);
    setErrorMessage('');
    setSendError('');

    try {
      const fxApiUrl = process.env.REACT_APP_FX_API_URL;
      const executeResult = await callApiSecure(`${fxApiUrl}cross-border/quote/execute`, httpMethods.post, Cognito, executePayload, 0);
      const initialSteps = Array.isArray(executeResult)
        ? executeResult
        : executeResult?.steps || crossBorderQuote?.steps || [];
      dispatch(transactionsActions.getTransactions());
      setTransferSteps(initialSteps);
      setTransferRequestId(requestId);
      activeExecuteRequestIdRef.current = null;
      setCurrentStep(4);
    } catch (error) {
      if (isDuplicateExecuteError(error)) {
        dispatch(transactionsActions.getTransactions());
        setTransferRequestId(requestId);
        activeExecuteRequestIdRef.current = null;
        setCurrentStep(4);
        return;
      }

      if (error?.statusCode) {
        activeExecuteRequestIdRef.current = null;
      }
      if (isComplianceException(error)) {
        setComplianceBlockedQuoteId(crossBorderQuoteId);
      }
      setSendError(getCrossBorderExecuteErrorMessage(error));
    } finally {
      executeInFlightRef.current = false;
      setExecutingTransfer(false);
    }
  };

  return (
    <Layout
      hideHeader
      showUserControls={false}
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={floatBackConfig}
    >
      <div className='flex w-full max-w-[1180px] flex-col gap-7'>
        {currentStep !== 4 && (
          <div className='mb-3 flex items-start justify-between gap-6'>
            <div className='min-w-0 flex-1'>
              <h1 className='m-0 font-display text-[38px] font-normal leading-[1.1] tracking-[-0.02em] text-[var(--n900)]'>{pageTitle}</h1>
              {renderPageSubtitle()}
            </div>
          </div>
        )}

        <ErrorToast
          error={
            getCrossBorderQuoteError || (errorMessage ? { detail: errorMessage } : null) || (validationError ? { detail: validationError } : null)
          }
          onDismiss={() => {
            setErrorMessage('');
            setValidationError(null);
            dispatch(crossBorderActions.clearCrossBorderQuote());
          }}
        />

        <div className='mx-auto flex w-full max-w-[680px] flex-col gap-[26px]'>
          {currentStep >= 1 && currentStep !== 4 && <SendMoneyStepper currentStep={currentStep} />}

          {currentStep === 0 && !fromDashboardCalculator && (
            <SendMoneyEntryStep
              parsingDocument={parsingDocument}
              parsingError={parsingError}
              transactionDocumentFileName={transactionDocumentFileName}
              onFileSelect={handleEntryFileSelect}
              onStartFromScratch={handleStartFromScratch}
              prefillCurrency={recipientCurrencyFilter}
            />
          )}

          {currentStep === 4 && transferRequestId && (
            <SendMoneyTransferProgressStep
              requestId={transferRequestId}
              steps={transferSteps}
              recipientName={selectedRecipientData?.name}
              destinationCountry={destinationCountryForGlobe}
              destinationCountryCode={destinationCountryForGlobe?.code || selectedCountryInfo?.code}
              receiveAmount={amountReceive ?? receiveAmountNum}
              receiveCurrency={receiveCurrency}
              sendAmount={totalCost ?? sendAmountNum}
              sendCurrency={sendCurrency}
              paymentReference={paymentReference}
              paymentPurpose={paymentPurposeLabel}
              transferFee={transferFee}
              exchangeRate={exchangeRateLabel}
              paymentDocumentFileName={transactionDocumentFileName}
              onBackHome={handleTransferBackHome}
            />
          )}

          {currentStep === 1 && (
            <RecipientSelectStep
              recipients={recipients}
              transactions={transactions}
              configCurrencies={configCurrencies}
              selectedRecipientId={selectedRecipient}
              gettingRecipients={gettingRecipients}
              currencyFilter={recipientCurrencyFilter}
              matchNotice={pendingInvoiceExtractedData ? parsingError : ''}
              onSelect={handleSelectRecipient}
              onAddNew={(currencyCode) => {
                if (currencyCode) {
                  navigate(`/recipient-type?currency=${encodeURIComponent(currencyCode)}`);
                  return;
                }
                navigate('/select-currency');
              }}
            />
          )}

          {currentStep === 2 && selectedRecipient && selectedRecipientData && (
            <SendMoneyAmountStep
              sendCurrency={sendCurrency}
              receiveCurrency={receiveCurrency}
              sendAmount={sendAmount}
              receiveAmount={receiveAmount}
              sendCurrencies={sendCurrencies}
              receiveCurrencies={receiveCurrencies}
              onSendCurrencyChange={setSendCurrency}
              onSendAmountChange={handleSendAmountChange}
              onReceiveAmountChange={handleReceiveAmountChange}
              spotRate={spotRate}
              gettingSpotRate={gettingSpotRate}
              showFxBar={showFxBar}
              balances={balances}
              sendAmountNum={sendAmountNum}
              formatCurrency={formatCurrencyAmount}
              isSwiftTransfer={isSwiftTransfer}
              parsingDocument={parsingDocument}
              parsingError={parsingError}
              transactionDocumentFileName={transactionDocumentFileName}
              transactionDocumentType={transactionDocumentType}
              transactionDocumentId={transactionDocumentId}
              onDocumentTypeChange={setTransactionDocumentType}
              onDocumentIdChange={setTransactionDocumentId}
              onFileInputChange={handleFileInputChange}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              renderUploadContent={renderUploadContent}
              paymentReference={paymentReference}
              onPaymentReferenceChange={setPaymentReference}
              isPaymentReferenceValid={isPaymentReferenceValid}
              paymentPurpose={paymentPurpose}
              onPaymentPurposeChange={setPaymentPurpose}
              paymentPurposeOptions={paymentPurposeOptions}
              onContinue={handleGetQuote}
              gettingCrossBorderQuote={gettingCrossBorderQuote}
              canContinue={canContinueToReview}
              startInEditMode={amountStartInEditMode}
            />
          )}

          {currentStep === 3 && selectedRecipient && selectedRecipientData && (
            <SendMoneyReviewStep
              recipient={selectedRecipientData}
              receiveCurrency={receiveCurrency}
              sendCurrency={sendCurrency}
              amountReceive={amountReceive ?? receiveAmountNum}
              totalCost={totalCost}
              sendAmountNum={sendAmountNum}
              accountDisplay={formatAccountDisplay(selectedRecipientData)}
              exchangeRateLabel={exchangeRateLabel}
              exchangeRateSubLabel={exchangeRateSubLabel}
              countdownDisplay={countdownDisplay}
              isQuoteExpired={isQuoteExpired}
              transferFee={transferFee}
              feePercent={feePercent}
              paymentReference={paymentReference}
              paymentPurposeLabel={paymentPurposeLabel}
              onEditAmount={handleEditAmountFromReview}
              onRequote={handleGetQuote}
              onSend={handleSendMoney}
              canSend={canSendTransfer}
              sendDisabledReason={sendDisabledReason}
              quoteRefreshNeeded={isQuoteExpired || Boolean(quoteInputsChanged)}
              executingTransfer={executingTransfer}
              sendError={sendError}
              hasQuote={hasReviewQuote}
              isLoadingQuote={isLoadingReviewQuote || gettingCrossBorderQuote}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

export default function SendMoneyPage() {
  const { state } = useLocation();
  return <SendMoney key={state?.sendMoneyReset ?? 'initial'} />;
}
